import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    // Chrome on Android fires 'cancel' on the dialog for back gestures instead of popstate.
    // Prevent native close so our closeDialog() runs and body scroll lock is properly cleared.
    this.refs.dialog?.addEventListener('cancel', this.#handleDialogCancel);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.refs.dialog?.removeEventListener('cancel', this.#handleDialogCancel);
    this.#historyAbortController?.abort();
  }

  #handleDialogCancel = (event) => {
    // Prevent the browser from closing the dialog natively (which skips our cleanup).
    // closeDialog() will handle body scroll lock removal and scroll restoration;
    // #handleHistoryClose will call history.back() to pop the cartDrawerOpen state.
    event.preventDefault();
    this.closeDialog();
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      // history.back() resets scroll; the browser may also kick off a smooth scroll restoration
      // (via `scroll-behavior: smooth` on <html>) that starts after the popstate fires.
      // We capture position, disable smooth scroll, register the listener BEFORE back(),
      // restore position in the popstate handler, then cancel any smooth restoration that
      // the browser starts in the frame after we re-enable smooth behavior.
      const scrollY = window.scrollY;
      history.scrollRestoration = 'manual';
      document.documentElement.style.scrollBehavior = 'auto';
      window.addEventListener('popstate', () => {
        window.scrollTo({ top: scrollY, behavior: 'instant' });
        requestAnimationFrame(() => {
          document.documentElement.style.scrollBehavior = '';
          history.scrollRestoration = 'auto';
          requestAnimationFrame(() => {
            window.scrollTo({ top: scrollY, behavior: 'instant' });
          });
        });
      }, { once: true });
      history.back();
    }
  };

  #handlePopState = async () => {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    // Body is position:fixed only while this drawer's scroll lock is active.
    // If it isn't set there's nothing to clean up — bail early.
    if (document.body.style.position !== 'fixed') return;

    // Recover scroll position from body.style.top before clearing it.
    // window.scrollY is 0 while body is fixed; body.style.top = '-{scrollY}px' stores the real value.
    const scrollY = -parseFloat(document.body.style.top || '0');
    history.scrollRestoration = 'manual';
    document.documentElement.style.scrollBehavior = 'auto';

    if (dialog.open) {
      // Normal path: dialog is still open — close it through our mechanism (clears body scroll lock).
      dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      dialog.style.removeProperty('--dialog-drawer-closing-animation');
    } else {
      // iOS Safari closes the modal dialog natively before firing popstate, leaving body scroll
      // lock active because our closeDialog() never ran. Clear it manually.
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      this.#historyAbortController?.abort();
    }

    // Same double-rAF pattern as #handleHistoryClose: restore scroll instantly, then cancel any
    // browser smooth-scroll restoration that starts in the frame after smooth behavior is restored.
    window.scrollTo({ top: scrollY, behavior: 'instant' });
    requestAnimationFrame(() => {
      document.documentElement.style.scrollBehavior = '';
      history.scrollRestoration = 'auto';
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      });
    });
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
